import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { formatDate } from '../utils/dateUtils';
import { Modal } from '../components/common/Modal';
import { Users, Plus, Copy, Trash2, Edit2, Shield, Eye, Pencil, Crown, Mail, Link } from 'lucide-react';
import toast from 'react-hot-toast';

const ROLES = [
  { value: 'admin', label: 'Admin', desc: 'Full access to all vehicles and records, can invite members', color: '#4f8ef7', icon: Shield },
  { value: 'editor', label: 'Editor', desc: 'Can add and edit records but cannot delete or manage team', color: '#22c55e', icon: Pencil },
  { value: 'viewer', label: 'Viewer', desc: 'Read-only access to assigned vehicles', color: '#a855f7', icon: Eye },
];

const RoleBadge = ({ role }) => {
  const r = ROLES.find(r => r.value === role) || ROLES[2];
  return (
    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${r.color}22`, color: r.color, border: `1px solid ${r.color}44`, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <r.icon size={10} /> {r.label}
    </span>
  );
};

export const Team = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [inviteLink, setInviteLink] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    const [m, i, v] = await Promise.all([
      supabase.from('team_members').select('*').eq('owner_id', user.id).order('created_at', { ascending: false }),
      supabase.from('team_invitations').select('*').eq('owner_id', user.id).eq('status', 'pending').order('created_at', { ascending: false }),
      supabase.from('vehicles').select('id,registration_number,brand,model').in('status', ['active','inactive','classic','daneira','immobilized','dokimi_test','service','restoration']),
    ]);
    setMembers(m.data || []);
    setInvitations(i.data || []);
    setVehicles(v.data || []);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { fetch(); }, [fetch]);

  const removeMember = async (id) => {
    if (!window.confirm('Remove this team member?')) return;
    await supabase.from('team_members').delete().eq('id', id);
    toast.success('Member removed');
    fetch();
  };

  const cancelInvitation = async (id) => {
    if (!window.confirm('Cancel this invitation?')) return;
    await supabase.from('team_invitations').delete().eq('id', id);
    toast.success('Invitation cancelled');
    fetch();
  };

  const updateRole = async (id, role) => {
    await supabase.from('team_members').update({ role }).eq('id', id);
    toast.success('Role updated');
    fetch();
  };

  const generateInviteLink = async (role) => {
    const { data, error } = await supabase.from('team_invitations').insert({
      owner_id: user.id,
      email: `invite-link-${Date.now()}@placeholder.com`,
      role,
    }).select().single();
    if (error) { toast.error('Failed to generate link'); return; }
    const link = `${window.location.origin}/join?token=${data.token}`;
    setInviteLink(link);
    setShowLinkModal(true);
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Team Management</div>
          <div className="page-subtitle">{members.length} active member{members.length !== 1 ? 's' : ''} · {invitations.length} pending invitation{invitations.length !== 1 ? 's' : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => setShowLinkModal(true)}>
            <Link size={14} /> Share Invite Link
          </button>
          <button className="btn btn-primary" onClick={() => setShowInviteModal(true)}>
            <Plus size={14} /> Invite Member
          </button>
        </div>
      </div>

      {/* Role overview */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Owner', count: 1, color: '#eab308', icon: Crown, desc: 'You — full access' },
          ...ROLES.map(r => ({ label: r.label, count: members.filter(m => m.role === r.value).length, color: r.color, icon: r.icon, desc: r.desc })),
        ].map((item, i) => (
          <div key={i} className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: `${item.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <item.icon size={14} style={{ color: item.color }} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: item.color }}>{item.label}</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, background: `linear-gradient(135deg, ${item.color}, ${item.color}99)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{item.count}</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>{item.desc}</div>
          </div>
        ))}
      </div>

      {/* Active members */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Users size={16} style={{ color: 'var(--accent)' }} />
          <span style={{ fontWeight: 700, fontSize: 17 }}>Active Members</span>
        </div>

        {members.length === 0 ? (
          <div className="empty-state">
            <Users />
            <h3>No team members yet</h3>
            <p>Invite your first team member using the button above.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Member</th><th>Role</th><th>Status</th><th>Joined</th><th>Assigned Vehicles</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.id}>
                    <td className="td-primary">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #4f8ef7, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white', flexShrink: 0 }}>
                          {m.email?.[0]?.toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{m.email}</div>
                          <div style={{ fontSize: 13, color: 'var(--text2)' }}>Team member</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <select
                        className="form-input"
                        style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }}
                        value={m.role}
                        onChange={e => updateRole(m.id, e.target.value)}
                      >
                        {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </td>
                    <td>
                      <span className={`badge badge-${m.status === 'active' ? 'green' : m.status === 'suspended' ? 'red' : 'gray'}`}>
                        {m.status}
                      </span>
                    </td>
                    <td>{formatDate(m.joined_at)}</td>
                    <td>
                      <button className="btn btn-ghost btn-sm" onClick={() => setShowAssignModal(m)}>
                        <Edit2 size={11} /> Manage
                      </button>
                    </td>
                    <td>
                      <button className="btn btn-danger btn-sm" onClick={() => removeMember(m.id)}>
                        <Trash2 size={11} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pending invitations */}
      {invitations.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Mail size={16} style={{ color: 'var(--orange)' }} />
            <span style={{ fontWeight: 700, fontSize: 17 }}>Pending Invitations</span>
            <span className="badge badge-orange" style={{ marginLeft: 'auto' }}>{invitations.length}</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Email</th><th>Role</th><th>Invited</th><th>Expires</th><th>Actions</th></tr></thead>
              <tbody>
                {invitations.map(inv => (
                  <tr key={inv.id}>
                    <td className="td-primary">{inv.email.includes('@placeholder.com') ? 'Invite Link' : inv.email}</td>
                    <td><RoleBadge role={inv.role} /></td>
                    <td>{formatDate(inv.created_at)}</td>
                    <td>{formatDate(inv.expires_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => {
                          const link = `${window.location.origin}/join?token=${inv.token}`;
                          navigator.clipboard.writeText(link);
                          toast.success('Link copied!');
                        }}>
                          <Copy size={11} /> Copy Link
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => cancelInvitation(inv.id)}>
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {showInviteModal && (
        <InviteModal
          user={user}
          onClose={() => { setShowInviteModal(false); fetch(); }}
        />
      )}

      {showAssignModal && (
        <AssignVehiclesModal
          member={showAssignModal}
          vehicles={vehicles}
          ownerId={user.id}
          onClose={() => { setShowAssignModal(null); fetch(); }}
        />
      )}

      {showLinkModal && (
        <ShareLinkModal
          inviteLink={inviteLink}
          onGenerate={generateInviteLink}
          onClose={() => { setShowLinkModal(false); setInviteLink(''); fetch(); }}
        />
      )}
    </div>
  );
};

// ─── Invite Modal ─────────────────────────────────────────────────────────────
const InviteModal = ({ user, onClose }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter an email'); return; }
    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('send-invitation', {
        body: { email, role, ownerName: user.email },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error || !data.success) { toast.error(error?.message || data?.error || 'Failed to send'); setSending(false); return; }
      toast.success(`Invitation sent to ${email}!`);
      onClose();
    } catch (err) {
      toast.error('Failed: ' + err.message);
    }
    setSending(false);
  };

  return (
    <Modal title="Invite Team Member" onClose={onClose}
      footer={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" form="invite-form" type="submit" disabled={sending}>{sending ? 'Sending…' : 'Send Invitation'}</button></>}>
      <form id="invite-form" onSubmit={send} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="form-group">
          <label className="form-label">Email Address *</label>
          <input className="form-input" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="colleague@company.com" />
        </div>
        <div className="form-group">
          <label className="form-label">Role *</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ROLES.map(r => (
              <label key={r.value} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 8, border: `1px solid ${role === r.value ? r.color : 'var(--border)'}`, background: role === r.value ? `${r.color}11` : 'var(--surface2)', cursor: 'pointer', transition: 'all 0.15s' }}>
                <input type="radio" name="role" value={r.value} checked={role === r.value} onChange={() => setRole(r.value)} style={{ accentColor: r.color }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: role === r.value ? r.color : 'var(--text)' }}>{r.label}</div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>{r.desc}</div>
                </div>
                <r.icon size={16} style={{ color: role === r.value ? r.color : 'var(--text3)' }} />
              </label>
            ))}
          </div>
        </div>
      </form>
    </Modal>
  );
};

// ─── Share Link Modal ─────────────────────────────────────────────────────────
const ShareLinkModal = ({ inviteLink, onGenerate, onClose }) => {
  const [role, setRole] = useState('viewer');
  const [generating, setGenerating] = useState(false);

  return (
    <Modal title="Share Invite Link" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ padding: '12px 14px', background: 'var(--surface2)', borderRadius: 8, fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
          Generate a shareable link that anyone can use to join your team. You can choose the role they'll receive when they join.
        </div>

        <div className="form-group">
          <label className="form-label">Role for this link</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {ROLES.map(r => (
              <button key={r.value} onClick={() => setRole(r.value)} style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', border: `1px solid ${role === r.value ? r.color : 'var(--border)'}`, background: role === r.value ? `${r.color}22` : 'var(--surface2)', color: role === r.value ? r.color : 'var(--text3)', transition: 'all 0.15s' }}>
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <button className="btn btn-primary" style={{ justifyContent: 'center' }} disabled={generating} onClick={async () => { setGenerating(true); await onGenerate(role); setGenerating(false); }}>
          <Link size={14} /> {generating ? 'Generating…' : 'Generate Invite Link'}
        </button>

        {inviteLink && (
          <div style={{ background: 'var(--surface2)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>Invite link (expires in 7 days):</div>
            <div style={{ fontSize: 11, fontFamily: 'DM Mono,monospace', color: 'var(--accent)', wordBreak: 'break-all', marginBottom: 10 }}>{inviteLink}</div>
            <button className="btn btn-primary btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success('Link copied!'); }}>
              <Copy size={12} /> Copy Link
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

// ─── Assign Vehicles Modal ────────────────────────────────────────────────────
const AssignVehiclesModal = ({ member, vehicles, ownerId, onClose }) => {
  const [assignments, setAssignments] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase.from('vehicle_assignments').select('vehicle_id').eq('user_id', member.user_id).eq('owner_id', ownerId).then(({ data }) => {
      setAssignments((data || []).map(a => a.vehicle_id));
    });
  }, [member.user_id, ownerId]);

  const toggle = (vehicleId) => {
    setAssignments(prev => prev.includes(vehicleId) ? prev.filter(id => id !== vehicleId) : [...prev, vehicleId]);
  };

  const save = async () => {
    setSaving(true);
    // Delete all existing assignments for this member
    await supabase.from('vehicle_assignments').delete().eq('user_id', member.user_id).eq('owner_id', ownerId);
    // Insert new assignments
    if (assignments.length > 0) {
      await supabase.from('vehicle_assignments').insert(
        assignments.map(vehicleId => ({ owner_id: ownerId, vehicle_id: vehicleId, user_id: member.user_id }))
      );
    }
    toast.success('Vehicle assignments saved!');
    setSaving(false);
    onClose();
  };

  return (
    <Modal title={`Assign Vehicles — ${member.email}`} onClose={onClose}
      footer={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Assignments'}</button></>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        <div style={{ padding: '10px 12px', background: 'var(--surface2)', borderRadius: 8, fontSize: 14, color: 'var(--text2)', marginBottom: 12 }}>
          Select which vehicles this member can access. Admins always see all vehicles regardless of assignments.
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 14, color: 'var(--text2)' }}>{assignments.length} of {vehicles.length} selected</span>
          <div style={{ display: 'flex', gap: 10 }}>
            <span style={{ fontSize: 12, color: 'var(--accent)', cursor: 'pointer' }} onClick={() => setAssignments(vehicles.map(v => v.id))}>Select all</span>
            <span style={{ fontSize: 14, color: 'var(--text2)', cursor: 'pointer' }} onClick={() => setAssignments([])}>Clear</span>
          </div>
        </div>
        {vehicles.map(v => (
          <label key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, border: `1px solid ${assignments.includes(v.id) ? 'var(--accent)' : 'var(--border)'}`, background: assignments.includes(v.id) ? 'var(--accent-glow)' : 'var(--surface2)', cursor: 'pointer', marginBottom: 6, transition: 'all 0.15s' }}>
            <input type="checkbox" checked={assignments.includes(v.id)} onChange={() => toggle(v.id)} style={{ accentColor: 'var(--accent)', width: 15, height: 15 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{v.brand} {v.model}</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', fontFamily: 'DM Mono,monospace' }}>{v.registration_number}</div>
            </div>
          </label>
        ))}
      </div>
    </Modal>
  );
};